import { useState } from 'react';
import Modal from './Modal';
import Button from './Button';
import CopyIcon from './CopyIcon';
import ShareIcon from './ShareIcon';
import copyToClipboard from 'copy-to-clipboard';
import { leaveLeagueMutation } from '../resources/League/mutations';
import { makeLink } from '../util/makeLink';
import OneLeagueResource from '../resources/League/One';
import AllUserProfilesResource from '../resources/AllUserProfiles';
import useCopy from '../util/useCopy';
import useShare from '../util/useShare';
import { getIsProfileFinished } from '../resources/Auth/services';
import { Header } from './Header';
import LogOutIcon from './LogOutIcon';
import CheckCircleIcon from './CheckCircleIcon';
import ViewBallotModal from './ViewBallotModal';
import ComposeBallotModal from './ComposeBallotModal';
import { Suspense } from 'react';
import Loader from './Loader';
import { IconSettings } from './IconSettings';
import LeagueSettingsModal from './LeagueSettingsModal';
import Action from './Action';
import QuestionCircleIcon from './QuestionCircleIcon';
import { makeClassName, pluralize } from '@siteless/util';
import BallotsCompletedResource from '../resources/Ballot/Completed';
import BallotsStartedResource from '../resources/Ballot/Started';
import { CurrentUserMustResource } from '../resources/Auth/CurrentUserMust';
import { UserListItem } from './UserListItem';
import QrCodeIcon from './QrCodeIcon';
import QrCodeModal from './QrCodeModal';

let LeagueModalContent = ({
  onClose,
  leagueId,
}: {
  onClose: () => void;
  leagueId?: string;
}) => {
  let copy = useCopy();
  let currentUser = CurrentUserMustResource.use();
  let league = OneLeagueResource.use(
    // @ts-expect-error resources doesn't type the null arg properly
    leagueId || null,
  );

  let allUserProfiles = AllUserProfilesResource.use();
  let completed = BallotsCompletedResource.use();
  let started = BallotsStartedResource.use();

  let [focusedBallotId, setFocusedBallotId] = useState<string | undefined>();
  let [isCurrentUserBallotFocused, setIsCurrentUserBallotFocused] =
    useState(false);
  let [showLeagueSettingsModal, setShowLeagueSettingsModal] = useState(false);
  let [showQrCodeModal, setShowQrCodeModal] = useState(false);
  let { share, isSharing, isSupported: isShareSupported } = useShare();

  // in cases where null is passed to league, it will be undefined
  if (league === undefined) return null;

  if (league === null)
    return (
      <div className="relative flex h-full flex-col">
        <div className="flex-none bg-white">
          <Header className="mx-4 border-b-4 border-neutral-100 px-2 py-4">
            {copy.League}
          </Header>
        </div>
        <div className="min-h-0 flex-1 overflow-auto p-12">
          {copy.thereSNoLeagueHereItWasProbablyDeleted}
        </div>
      </div>
    );

  let isCurrentUserOwner = league.ownerUserId === currentUser.id;

  let visibleMembers = league.memberUserIds
    .filter((userId) => getIsProfileFinished(allUserProfiles.users[userId]))
    .sort((a, b) => {
      if (a === currentUser.id) return -1;
      if (b === currentUser.id) return 1;

      let aCompleted = completed[a] || false;
      let bCompleted = completed[b] || false;

      if (aCompleted && !bCompleted) return -1;
      if (bCompleted && !aCompleted) return 1;

      return a.localeCompare(b);
    })
    .filter((userId) => allUserProfiles.users?.[userId]);

  return (
    <div className="relative flex h-full flex-col">
      {isCurrentUserOwner && leagueId && (
        <LeagueSettingsModal
          isOpen={showLeagueSettingsModal}
          onClose={() => {
            setShowLeagueSettingsModal(false);
          }}
          leagueId={leagueId}
        />
      )}
      {league?.joinLinkId && (
        <QrCodeModal
          isOpen={showQrCodeModal}
          onClose={() => setShowQrCodeModal(false)}
          url={makeLink(league.joinLinkId)}
          leagueName={league.name}
        />
      )}
      <ComposeBallotModal
        isOpen={isCurrentUserBallotFocused}
        onClose={() => {
          setIsCurrentUserBallotFocused(false);
        }}
      />
      {focusedBallotId && (
        <ViewBallotModal
          ballotKey={focusedBallotId}
          isOpen
          onClose={() => {
            setFocusedBallotId(undefined);
          }}
        />
      )}
          <div className="flex-none bg-white">
            <Header className="mx-4 border-b-4 border-neutral-100 px-2 py-4 pr-10">
              {league.name}
            </Header>
          </div>
          <div className="min-h-0 flex-1 overflow-auto p-12 pt-4">
            <div className="flex w-full items-center justify-center font-medium">
              {pluralize({
                count: league.memberUserIds.length,
                singular: copy.member,
              })}
            </div>
            {league.memberUserIds.filter((userId) => !started[userId]).length > 0 && (
              <div className="flex w-full items-center justify-center text-xs font-medium text-neutral-700">
                ({league.memberUserIds.filter((userId) => !started[userId]).length}{' '}
                {pluralize({
                  count: league.memberUserIds.filter((userId) => !started[userId]).length,
                  singular: copy['hasnt started a ballot'],
                  plural: copy['havent started a ballot'],
                  hideCount: true,
                })})
              </div>
            )}
            {league.strictMode && (
              <div className="flex w-full items-center justify-center">
                {copy.StrictScoringIsOn}{' '}
                <Action
                  Icon={QuestionCircleIcon}
                  onClick={() => {
                    window.alert(copy.strictScoringExplanation);
                  }}
                />
              </div>
            )}
            <div className="h-8"></div>
            {visibleMembers.map((userId, index) => {
              let isComplete = completed[userId];
              return (
                <UserListItem
                  className={makeClassName(
                    'relative mx-auto mb-8',
                    index % 2 ? 'left-12' : 'right-12',
                  )}
                  user={allUserProfiles.users[userId]}
                  isComplete={isComplete}
                  isCurrentUser={userId === currentUser.id}
                  isOwner={userId === league.ownerUserId}
                  key={userId}
                  onClick={() => {
                    if (userId === currentUser.id)
                      setIsCurrentUserBallotFocused(true);
                    else setFocusedBallotId(userId);
                  }}
                />
              );
            })}
          </div>
          <div className="flex-none bg-white">
            <div className="mx-4 flex border-t-4 border-neutral-100 py-4">
              {isShareSupported ? (
                <Button
                  className={league?.joinLinkId ? 'flex-1 rounded-r-none' : 'flex-1'}
                  disabled={isSharing}
                  onClick={() =>
                    share({
                      title: copy.joinMyFantasyOscarsLeague,
                      url: makeLink(league?.joinLinkId || ''),
                    })
                  }
                  Icon={ShareIcon}
                >
                  {copy.invite}
                </Button>
              ) : (
                <Button
                  className={league?.joinLinkId ? 'flex-1 rounded-r-none' : 'flex-1'}
                  onClick={() => {
                    copyToClipboard(makeLink(league?.joinLinkId || ''));
                  }}
                  AcknowledgeClickIcon={CheckCircleIcon}
                  Icon={CopyIcon}
                >
                  {copy.invite}
                </Button>
              )}
              {league?.joinLinkId && (
                <Button
                  className="flex-none rounded-l-none border-l-0"
                  Icon={QrCodeIcon}
                  onClick={() => setShowQrCodeModal(true)}
                />
              )}
              {isCurrentUserOwner ? (
                <Button
                  className="ml-2 flex-none"
                  Icon={IconSettings}
                  onClick={() => {
                    setShowLeagueSettingsModal(true);
                  }}
                />
              ) : (
                <Button
                  className="ml-2 flex-none"
                  Icon={LogOutIcon}
                  onClick={() => {
                    if (
                      !window.confirm(
                        `${copy.areYouSureYouWantToLeave} "${league?.name}"?`,
                      )
                    )
                      return;

                    if (typeof leagueId === 'string')
                      leaveLeagueMutation({ userId: currentUser.id, leagueId });
                    onClose();
                  }}
                />
              )}
            </div>
          </div>
    </div>
  );
};

let LeagueModal = ({
  isOpen,
  onClose,
  leagueId,
}: {
  isOpen: boolean;
  onClose: () => void;
  leagueId?: string;
}) => (
  <Modal isOpen={isOpen} onClose={onClose} disableBodyPadding className="LeagueModal flex">
    <Suspense fallback={<Loader />}>
      <LeagueModalContent onClose={onClose} leagueId={leagueId} />
    </Suspense>
  </Modal>
);

export default LeagueModal;
